import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { formatLongDate } from '../../../utils/dateFormatter';

const BookingDetailModal = ({ booking, onClose }) => {
  if (!booking) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <CardHeader className="border-b">
          <div className="flex justify-between items-start">
            <div>
              <CardTitle>Booking Details</CardTitle>
              <CardDescription>ID: {booking.id}</CardDescription>
            </div>
            <Button variant="ghost" size="sm" onClick={onClose}>✕</Button>
          </div>
        </CardHeader>
        <CardContent className="p-6 space-y-4">
          {/* Customer & Vendor Info */}
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-blue-50 p-3 rounded-lg">
              <p className="text-xs text-blue-600 font-medium">Customer</p>
              <p className="font-semibold">{booking.customer_name}</p>
              <p className="text-sm text-gray-600">{booking.customer_email}</p>
            </div>
            <div className="bg-green-50 p-3 rounded-lg">
              <p className="text-xs text-green-600 font-medium">Vendor</p>
              <p className="font-semibold">{booking.vendor_name}</p>
              <p className="text-sm text-gray-600">{booking.business_name}</p>
            </div>
          </div>

          {/* Event Info */}
          <div className="bg-gray-50 p-3 rounded-lg">
            <p className="text-xs text-gray-600 font-medium">Event Details</p>
            <p className="font-medium">{booking.event_type || 'N/A'}</p>
            <p className="text-sm text-gray-600">{booking.location || 'N/A'}</p>
            {booking.event_date && (
              <p className="text-sm text-gray-600">Date: {formatLongDate(booking.event_date)}</p>
            )}
          </div>

          {/* Payment Info */}
          <div className="border rounded-lg p-3">
            <p className="text-xs text-gray-600 font-medium mb-2">Payment Summary</p>
            <div className="space-y-1 text-sm">
              <div className="flex justify-between">
                <span>Total Amount:</span>
                <span className="font-semibold">${booking.agreed_price?.toFixed(2)}</span>
              </div>
              <div className="flex justify-between">
                <span>Deposit ({((booking.deposit_amount / booking.agreed_price) * 100).toFixed(0)}%):</span>
                <span className={booking.deposit_paid ? 'text-green-600' : 'text-gray-500'}>
                  ${booking.deposit_amount?.toFixed(2)} {booking.deposit_paid ? '✓ Paid' : '- Pending'}
                </span>
              </div>
              <div className="flex justify-between">
                <span>Final Amount:</span>
                <span className={booking.final_paid ? 'text-green-600' : 'text-gray-500'}>
                  ${booking.final_amount?.toFixed(2)} {booking.final_paid ? '✓ Paid' : '- Pending'}
                </span>
              </div>
              <div className="flex justify-between border-t pt-1 mt-1">
                <span>Platform Fee:</span>
                <span>${booking.platform_fee?.toFixed(2)}</span>
              </div>
              <div className="flex justify-between">
                <span>Vendor Payout:</span>
                <span className="font-semibold">${booking.vendor_payout_amount?.toFixed(2)}</span>
              </div>
            </div>
          </div>

          {/* Status */}
          <div className="flex items-center justify-between bg-gray-50 p-3 rounded-lg">
            <div>
              <p className="text-xs text-gray-600">Status</p>
              <Badge className={
                booking.status === 'completed' ? 'bg-green-100 text-green-800' :
                booking.status?.includes('cancelled') ? 'bg-red-100 text-red-800' :
                'bg-blue-100 text-blue-800'
              }>
                {booking.status?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
              </Badge>
            </div>
            <div className="text-right text-sm">
              <p className="text-gray-500">Created: {new Date(booking.created_at).toLocaleDateString()}</p>
            </div>
          </div>

          {/* Cancellation Details */}
          {booking.cancelled_by && (
            <div className="bg-red-50 border border-red-200 p-3 rounded-lg">
              <p className="text-xs text-red-600 font-medium">Cancellation Details</p>
              <p className="text-sm"><strong>Cancelled by:</strong> {booking.cancelled_by}</p>
              <p className="text-sm"><strong>Reason:</strong> {booking.cancellation_reason || 'N/A'}</p>
              <p className="text-sm"><strong>Refund Status:</strong> {booking.refund_status || 'N/A'}</p>
              {booking.refund_amount > 0 && (
                <p className="text-sm"><strong>Refund Amount:</strong> ${booking.refund_amount?.toFixed(2)}</p>
              )}
            </div>
          )}

          {/* Service Agreement */}
          <div className="text-sm text-gray-600">
            <p>Service Agreement: 
              Customer {booking.service_agreement_accepted_by_customer ? '✓' : '○'} | 
              Vendor {booking.service_agreement_accepted_by_vendor ? '✓' : '○'}
            </p>
          </div>

          {/* Actions */}
          <div className="flex gap-2 pt-2">
            <Button variant="outline" className="flex-1" onClick={onClose}>
              Close
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default BookingDetailModal;
